/**
 * The one place the product's name lives (ADR-860). Everything users and operators see takes the name from
 * here: the web app, emails, notifications, PDF metadata, exports, the webhook user agent, log lines.
 *
 * - `DEFAULT_APP_NAME` is the only literal. `scripts/rename-product.mjs` rewrites it when the product is renamed.
 * - Operators override it per deployment with the `APP_NAME` environment variable (the API calls `setAppName`
 *   at boot; the web app calls it from `/api/public-config` before it renders).
 * - UI strings never contain the name: write `{appName}` in a catalog string. `createTranslator` and the web
 *   `t()` fill it in through `fillText`.
 */
export const DEFAULT_APP_NAME = 'BOLTcrm';

/**
 * The current name as a live binding: `t('key', { app: APP_NAME })` and `{appName}` both read it, so there is one
 * source of truth. Prefer `appName()` in server code; `setAppName` is the only writer.
 */
export let APP_NAME: string = DEFAULT_APP_NAME;

/** Sets the name for this process (API) or page (web). Empty or missing falls back to the default. */
export function setAppName(name?: string | null): void {
  const trimmed = name?.trim();
  APP_NAME = trimmed ? trimmed : DEFAULT_APP_NAME;
}

/** The product name to show now: the operator's `APP_NAME` when set, otherwise the default. */
export const appName = (): string => APP_NAME;

/** A token for header values, file names and user agents: the name without spaces or punctuation. */
export const appToken = (): string => APP_NAME.replace(/[^A-Za-z0-9]+/g, '') || DEFAULT_APP_NAME;

/** Fills `{name}` placeholders from `vars`; `{appName}` is always available. Unknown placeholders stay as written. */
export function fillText(text: string, vars?: Record<string, string | number>): string {
  // A missing catalog entry or key must not take a whole page down: hand back what we were given.
  if (typeof text !== 'string' || !text.includes('{')) return text;
  return text.replace(/\{(\w+)\}/g, (match, key: string) => {
    if (vars && key in vars) return String(vars[key]);
    // `{app}` is the short spelling some strings use with `t('key', { app: APP_NAME })`; both give the same name.
    return key === 'appName' || key === 'app' ? APP_NAME : match;
  });
}
